import { z } from 'zod'

import {
  FileAreaSchema,
  FileDomainInsertModelSchema,
  FileDomainQueryModelSchema,
  FileDomainSelectModelSchema,
  FileDomainUpdateModelSchema,
  FileIdSchema,
  FileTaggingStatusSchema,
  FolderUrnSchema,
  NameSchema,
  NonEmptyStringSchema,
  ProjectIdSchema,
  PublicFileAreaSchema,
  UserIdSchema,
} from '@mntn-dev/domain-types'

import type { ZodSimplify } from '@mntn-dev/utility-types'
import { QuickframeTagsSchema } from './metatags/quickframe-tags.ts'
import { FileResourceTypeSchema } from './resource.ts'
import { StructuredMetadataSchema } from './structured-metadata.ts'
import {
  ImageUrlOptionsSchema,
  UrlOptionsSchema,
  VideoTransformationAndConfigAndUrlOptionsSchema,
} from './transformation-options-schema.ts'
import { UploadApiOptionsSchema } from './upload-api-options-schema.ts'

export const ValidForSecondsSchema = z
  .number()
  .int()
  .positive()
  .min(120)
  .max(36000)
export type ValidForSeconds = z.infer<typeof ValidForSecondsSchema>

export const GetUploadInfoInputSchema = z.object({
  fileArea: FileAreaSchema,
  fileResourceType: FileResourceTypeSchema,
  fileName: NameSchema,
  fileId: FileIdSchema,
})

export type GetUploadInfoInput = z.infer<typeof GetUploadInfoInputSchema>

export const RecordUploadInputSchema = FileDomainInsertModelSchema.partial({
  uploadStatus: true,
}).extend({
  folderUrn: FolderUrnSchema,
  fileResourceType: FileResourceTypeSchema,
  structuredMetadata: StructuredMetadataSchema.optional(),
})

export type RecordUploadInput = z.infer<typeof RecordUploadInputSchema>

export const RecordPublicUploadInputSchema = RecordUploadInputSchema.extend({
  area: PublicFileAreaSchema,
})

export type RecordPublicUploadInput = z.infer<
  typeof RecordPublicUploadInputSchema
>

export const CompleteUploadInputSchema = z.object({
  fileId: FileIdSchema,
})

export type CompleteUploadInput = z.infer<typeof CompleteUploadInputSchema>

export const SetTagStatusInput = z.object({
  fileId: FileIdSchema,
  fileTaggingStatus: FileTaggingStatusSchema,
  projectId: ProjectIdSchema.optional(),
})

export type SetTagStatusInput = z.infer<typeof SetTagStatusInput>

export const VerifyNotificationSignatureInputSchema = z.object({
  body: z.string(),
  timestamp: z.number().min(1),
  signature: z.string(),
  validFor: ValidForSecondsSchema.optional(),
})

export type VerifyNotificationSignatureInput = z.infer<
  typeof VerifyNotificationSignatureInputSchema
>

export const SignApiParametersParamsSchema = z.record(z.string(), z.any())
export type SignApiParametersParams = z.infer<
  typeof SignApiParametersParamsSchema
>

export const SignApiParametersInputSchema = z.object({
  fileArea: FileAreaSchema,
  params: SignApiParametersParamsSchema,
})

export type SignApiParametersInput = z.infer<
  typeof SignApiParametersInputSchema
>

export const SignApiParametersPublicInputSchema =
  SignApiParametersInputSchema.extend({
    fileArea: PublicFileAreaSchema,
  })

export type SignApiParametersPublicInput = z.infer<
  typeof SignApiParametersPublicInputSchema
>

export const ImageWidthSchema = z.coerce.number().positive()
export type ImageWidth = z.infer<typeof ImageWidthSchema>

export const ImageHeightSchema = z.coerce.number().positive()
export type ImageHeight = z.infer<typeof ImageHeightSchema>

export const GetAvatarUrlInputSchema = z.object({
  userId: UserIdSchema,
  options: ImageUrlOptionsSchema,
})

export type GetAvatarUrlInput = z.infer<typeof GetAvatarUrlInputSchema>

export const GetAssetDownloadUrlInputSchema = z.object({
  fileId: FileIdSchema,
  options: UrlOptionsSchema,
})

export type GetAssetDownloadUrlInput = z.infer<
  typeof GetAssetDownloadUrlInputSchema
>

export const GetImageDownloadUrlInputSchema = z.object({
  fileId: FileIdSchema,
  options: ImageUrlOptionsSchema,
})

export type GetImageDownloadUrlInput = z.infer<
  typeof GetImageDownloadUrlInputSchema
>

export const GetVideoUrlInputSchema = z.object({
  fileId: FileIdSchema,
  options: VideoTransformationAndConfigAndUrlOptionsSchema,
})

export type GetVideoUrlInput = z.infer<typeof GetVideoUrlInputSchema>

export const EditFileDetailsInputSchema = z.object({
  fileId: FileIdSchema,
  updates: FileDomainUpdateModelSchema.pick({
    description: true,
    title: true,
  }),
})

export type EditFileDetailsInput = z.infer<typeof EditFileDetailsInputSchema>

export const ArchiveFileInputSchema = z.object({
  fileId: FileIdSchema,
})

export type ArchiveFileInput = z.infer<typeof ArchiveFileInputSchema>

export const GetFileListInputSchema = z.object({
  where: z.object({
    folderUrn: FolderUrnSchema,
    includeChildFolders: z.boolean().optional(),
    area: FileAreaSchema.optional(),
    search: NonEmptyStringSchema().optional(),
  }),
  orderBy: z
    .object({
      column: FileDomainQueryModelSchema.keyof().extract(['uploadTimestamp']),
      direction: z.enum(['asc', 'desc']),
    })
    .optional(),
})

export type GetFileListInput = z.infer<typeof GetFileListInputSchema>

export const GetFileByIdInputSchema = z.object({
  fileId: FileIdSchema,
})

export type GetFileByIdInput = z.infer<typeof GetFileByIdInputSchema>

export const DownloadFileInputSchema = z.object({
  fileId: FileIdSchema,
})

export type DownloadFileInput = z.infer<typeof DownloadFileInputSchema>

export const OverwriteFileInputSchema = z.object({
  filePath: z.string(),
  fileId: FileIdSchema,
  options: UploadApiOptionsSchema.optional(),
})
export type OverwriteFileInput = z.infer<typeof OverwriteFileInputSchema>

export const TagFileInputSchema = z.object({
  fileId: FileIdSchema,
  tags: QuickframeTagsSchema.omit({ assetFileUuid: true }),
})

export type TagFileInput = z.infer<typeof TagFileInputSchema>

export const GetFileDeliverableInputSchema = z.object({
  fileId: FileIdSchema,
})

export type GetFileDeliverableInput = z.infer<
  typeof GetFileDeliverableInputSchema
>

export const LinkFileInputSchema = z.object({
  source: FileDomainSelectModelSchema.pick({ fileId: true }),
  target: FileDomainInsertModelSchema.pick({
    folderUrn: true,
    area: true,
  }).partial({
    area: true,
  }),
})

export type LinkFileInput = ZodSimplify<typeof LinkFileInputSchema>
